import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Key, Mail, CheckCircle, AlertCircle, Send, TestTube, Plus, Trash2, Edit2, Users, Star, StarOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [formData, setFormData] = useState({
    aabkari_api_key: '',
    aabkari_sender_email: 'info@aabkaritimes.com',
    aabkari_sender_name: 'Aabkari Times Magazine',
    sugar_api_key: '',
    sugar_sender_email: 'info@sugartimes.co.in',
    sugar_sender_name: 'Sugar Times Magazine'
  });
  const [configStatus, setConfigStatus] = useState({
    aabkari_configured: false,
    sugar_configured: false
  });
  const [testEmail, setTestEmail] = useState('');
  const [testMagazine, setTestMagazine] = useState('Aabkari Times');
  
  // Multi-sender accounts state
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [newAccount, setNewAccount] = useState({
    email: '',
    display_name: '',
    magazine: 'Aabkari Times',
    is_default: false
  });

  useEffect(() => {
    fetchSettings();
    fetchSenderAccounts();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConfigStatus({
        aabkari_configured: response.data.aabkari_configured || false,
        sugar_configured: response.data.sugar_configured || false
      });
      
      setFormData({
        aabkari_api_key: '',
        aabkari_sender_email: response.data.aabkari_sender_email || 'info@aabkaritimes.com',
        aabkari_sender_name: response.data.aabkari_sender_name || 'Aabkari Times Magazine',
        sugar_api_key: '',
        sugar_sender_email: response.data.sugar_sender_email || 'info@sugartimes.co.in',
        sugar_sender_name: response.data.sugar_sender_name || 'Sugar Times Magazine'
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchSenderAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email/sender-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSenderAccounts(response.data);
    } catch (error) {
      console.error('Error fetching sender accounts:', error);
    }
  };

  const initializeSenderAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/email/sender-accounts/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sender accounts initialized!');
      fetchSenderAccounts();
    } catch (error) {
      console.error('Error initializing accounts:', error);
      toast.error('Failed to initialize accounts');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/email/settings`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Email settings saved successfully!');
      fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    setTestLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/email/send-test`, {
        magazine: testMagazine,
        test_email: testEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestLoading(false);
    }
  };

  const handleAddAccount = async () => {
    if (!newAccount.email || !newAccount.display_name) {
      toast.error('Please fill in email and display name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/email/sender-accounts`, newAccount, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sender account added!');
      setNewAccount({ email: '', display_name: '', magazine: 'Aabkari Times', is_default: false });
      setShowAddAccount(false);
      fetchSenderAccounts();
    } catch (error) {
      console.error('Error adding account:', error);
      toast.error(error.response?.data?.detail || 'Failed to add account');
    }
  };

  const handleUpdateAccount = async () => {
    if (!editingAccount) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/email/sender-accounts/${editingAccount.id}`, {
        email: editingAccount.email,
        display_name: editingAccount.display_name,
        magazine: editingAccount.magazine,
        is_default: editingAccount.is_default
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sender account updated!');
      setEditingAccount(null);
      fetchSenderAccounts();
    } catch (error) {
      console.error('Error updating account:', error);
      toast.error(error.response?.data?.detail || 'Failed to update account');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this sender account?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/email/sender-accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sender account deleted!');
      fetchSenderAccounts();
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    }
  };

  const aabkariAccounts = senderAccounts.filter(a => a.magazine === 'Aabkari Times' || a.magazine === 'Aabkari Times ENG');
  const sugarAccounts = senderAccounts.filter(a => a.magazine === 'Sugar Times');

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      <Header />
      <div className="py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => navigate('/')}>
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>
              <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>
                Email Settings
              </h1>
            </div>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className={`p-4 rounded-lg border-2 ${configStatus.aabkari_configured ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
              <div className="flex items-center space-x-3">
                {configStatus.aabkari_configured ? (
                  <CheckCircle className="text-green-600" size={24} />
                ) : (
                  <AlertCircle className="text-yellow-600" size={24} />
                )}
                <div>
                  <p className="font-semibold" style={{ color: configStatus.aabkari_configured ? '#166534' : '#92400e' }}>
                    Aabkari Times
                  </p>
                  <p className="text-sm" style={{ color: configStatus.aabkari_configured ? '#16a34a' : '#ca8a04' }}>
                    {configStatus.aabkari_configured ? 'API Key Configured' : 'Not Configured'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className={`p-4 rounded-lg border-2 ${configStatus.sugar_configured ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
              <div className="flex items-center space-x-3">
                {configStatus.sugar_configured ? (
                  <CheckCircle className="text-green-600" size={24} />
                ) : (
                  <AlertCircle className="text-yellow-600" size={24} />
                )}
                <div>
                  <p className="font-semibold" style={{ color: configStatus.sugar_configured ? '#166534' : '#92400e' }}>
                    Sugar Times
                  </p>
                  <p className="text-sm" style={{ color: configStatus.sugar_configured ? '#16a34a' : '#ca8a04' }}>
                    {configStatus.sugar_configured ? 'API Key Configured' : 'Not Configured'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* =============================================
              MULTI-SENDER EMAIL ACCOUNTS SECTION
          ============================================= */}
          <div className="form-section mb-8 bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold" style={{ color: '#7c3aed' }}>
                  <Users className="inline mr-2" size={24} />
                  Sender Email Accounts
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Manage multiple sender emails for each magazine. Select which email to send from when composing emails.
                </p>
              </div>
              <div className="flex gap-2">
                {senderAccounts.length === 0 && (
                  <Button onClick={initializeSenderAccounts} variant="outline" className="border-purple-300 text-purple-700">
                    <Plus className="mr-1" size={16} />
                    Initialize Default Accounts
                  </Button>
                )}
                <Button onClick={() => setShowAddAccount(true)} style={{ backgroundColor: '#7c3aed' }}>
                  <Plus className="mr-1" size={16} />
                  Add Sender Account
                </Button>
              </div>
            </div>

            {/* Add New Account Form */}
            {showAddAccount && (
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-6">
                <h3 className="font-semibold text-purple-800 mb-3">Add New Sender Account</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm">Email Address</Label>
                    <Input
                      value={newAccount.email}
                      onChange={(e) => setNewAccount({ ...newAccount, email: e.target.value })}
                      placeholder="shashi@aabkaritimes.com"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Display Name</Label>
                    <Input
                      value={newAccount.display_name}
                      onChange={(e) => setNewAccount({ ...newAccount, display_name: e.target.value })}
                      placeholder="Shashi - Aabkari Times"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Magazine</Label>
                    <select
                      value={newAccount.magazine}
                      onChange={(e) => setNewAccount({ ...newAccount, magazine: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="Aabkari Times">Aabkari Times</option>
                      <option value="Sugar Times">Sugar Times</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">&nbsp;</Label>
                    <div className="flex gap-2">
                      <Button onClick={handleAddAccount} size="sm" style={{ backgroundColor: '#059669' }}>
                        <CheckCircle className="mr-1" size={14} />
                        Add
                      </Button>
                      <Button onClick={() => setShowAddAccount(false)} size="sm" variant="outline">
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
                <label className="flex items-center mt-3 text-sm text-purple-700">
                  <input
                    type="checkbox"
                    checked={newAccount.is_default}
                    onChange={(e) => setNewAccount({ ...newAccount, is_default: e.target.checked })}
                    className="mr-2"
                  />
                  Set as default sender for this magazine
                </label>
              </div>
            )}

            {/* Aabkari Times Accounts */}
            <div className="mb-6">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                <Mail className="mr-2" size={18} />
                Aabkari Times Sender Accounts ({aabkariAccounts.length})
              </h3>
              {aabkariAccounts.length === 0 ? (
                <p className="text-gray-500 text-sm italic">No sender accounts configured yet.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {aabkariAccounts.map(account => (
                    <div 
                      key={account.id} 
                      className={`p-3 rounded-lg border ${account.is_default ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'}`}
                    >
                      {editingAccount?.id === account.id ? (
                        <div className="space-y-2">
                          <Input
                            value={editingAccount.email}
                            onChange={(e) => setEditingAccount({ ...editingAccount, email: e.target.value })}
                            className="text-sm"
                          />
                          <Input
                            value={editingAccount.display_name}
                            onChange={(e) => setEditingAccount({ ...editingAccount, display_name: e.target.value })}
                            className="text-sm"
                          />
                          <label className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              checked={editingAccount.is_default}
                              onChange={(e) => setEditingAccount({ ...editingAccount, is_default: e.target.checked })}
                              className="mr-2"
                            />
                            Default
                          </label>
                          <div className="flex gap-2">
                            <Button onClick={handleUpdateAccount} size="sm" className="bg-green-600 text-xs">Save</Button>
                            <Button onClick={() => setEditingAccount(null)} size="sm" variant="outline" className="text-xs">Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm flex items-center">
                              {account.is_default && <Star className="mr-1 text-yellow-500" size={14} />}
                              {account.display_name}
                            </p>
                            <p className="text-xs text-gray-600">{account.email}</p>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setEditingAccount(account)}
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteAccount(account.id)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sugar Times Accounts */}
            <div>
              <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                <Mail className="mr-2" size={18} />
                Sugar Times Sender Accounts ({sugarAccounts.length})
              </h3>
              {sugarAccounts.length === 0 ? (
                <p className="text-gray-500 text-sm italic">No sender accounts configured yet.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {sugarAccounts.map(account => (
                    <div 
                      key={account.id} 
                      className={`p-3 rounded-lg border ${account.is_default ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}
                    >
                      {editingAccount?.id === account.id ? (
                        <div className="space-y-2">
                          <Input
                            value={editingAccount.email}
                            onChange={(e) => setEditingAccount({ ...editingAccount, email: e.target.value })}
                            className="text-sm"
                          />
                          <Input
                            value={editingAccount.display_name}
                            onChange={(e) => setEditingAccount({ ...editingAccount, display_name: e.target.value })}
                            className="text-sm"
                          />
                          <label className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              checked={editingAccount.is_default}
                              onChange={(e) => setEditingAccount({ ...editingAccount, is_default: e.target.checked })}
                              className="mr-2"
                            />
                            Default
                          </label>
                          <div className="flex gap-2">
                            <Button onClick={handleUpdateAccount} size="sm" className="bg-green-600 text-xs">Save</Button>
                            <Button onClick={() => setEditingAccount(null)} size="sm" variant="outline" className="text-xs">Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm flex items-center">
                              {account.is_default && <Star className="mr-1 text-yellow-500" size={14} />}
                              {account.display_name}
                            </p>
                            <p className="text-xs text-gray-600">{account.email}</p>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setEditingAccount(account)}
                              className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteAccount(account.id)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> All sender emails must be verified in your Brevo account before they can be used. 
                Go to Brevo → Settings → Senders & IP → Add sender for each email.
              </p>
            </div>
          </div>

          {/* Email Usage Guide */}
          <div className="form-section mb-8 bg-white p-6 rounded-lg shadow-sm border">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#7c3aed' }}>📧 Email Configuration Guide</h2>
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <h3 className="font-semibold mb-3 text-purple-800">Which Email ID is Used for Each Module:</h3>
              <table className="w-full text-sm">
                <thead className="bg-purple-100">
                  <tr>
                    <th className="p-2 text-left">Module</th>
                    <th className="p-2 text-left">Sender Selection</th>
                    <th className="p-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-2 font-medium">Ad Proposal</td>
                    <td className="p-2">Dropdown - Select from configured accounts</td>
                    <td className="p-2 text-gray-600">Can select per proposal</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2 font-medium">Renewal Reminder</td>
                    <td className="p-2">Dropdown - Select from configured accounts</td>
                    <td className="p-2 text-gray-600">Can select per batch</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2 font-medium">Greetings Email</td>
                    <td className="p-2">Uses default account</td>
                    <td className="p-2 text-gray-600">-</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-2 font-medium">News Broadcast</td>
                    <td className="p-2">Uses default account</td>
                    <td className="p-2 text-gray-600">-</td>
                  </tr>
                  <tr>
                    <td className="p-2 font-medium">Monthly Magazine</td>
                    <td className="p-2">Uses default account</td>
                    <td className="p-2 text-gray-600">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="bg-amber-50 p-6 rounded-lg border border-amber-200 mt-4">
              <h3 className="font-semibold mb-3 text-amber-800">📊 Brevo Daily Email Limits & Queue Status:</h3>
              <ul className="list-disc list-inside space-y-2 text-amber-900 text-sm">
                <li><strong>Free Plan:</strong> 300 emails/day</li>
                <li><strong>Starter Plan:</strong> 20,000 emails/month (~666/day)</li>
                <li><strong>Business Plan:</strong> Unlimited</li>
              </ul>
              <div className="mt-4 pt-4 border-t border-amber-200">
                <p className="text-sm text-amber-900">
                  <strong>To check quota & queue status:</strong><br/>
                  1. Login to <a href="https://app.brevo.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Brevo Dashboard</a><br/>
                  2. Go to <strong>Campaigns → Statistics</strong> to see sent/remaining quota<br/>
                  3. Go to <strong>Transactional → Logs</strong> to see queue status and delivery reports<br/>
                  4. Emails marked "Queued" will be sent when daily limit resets (midnight UTC)
                </p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="form-section mb-8 bg-white p-6 rounded-lg shadow-sm border">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#7c3aed' }}>How to Get Brevo API Keys</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <ol className="space-y-3">
                <li><strong>1.</strong> Go to <a href="https://www.brevo.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">brevo.com</a> and sign in to each account</li>
                <li><strong>2.</strong> Navigate to: <strong>Settings → SMTP & API → API Keys</strong></li>
                <li><strong>3.</strong> Click "Generate new API key" → Name it appropriately</li>
                <li><strong>4.</strong> Copy the key (starts with <code>xkeysib-</code>)</li>
                <li><strong>5.</strong> Paste below for each magazine and save!</li>
              </ol>
              <p className="mt-4 text-sm text-blue-700">
                <strong>Note:</strong> Each magazine needs its own Brevo account and API key for proper domain verification.
              </p>
            </div>
          </div>

          {/* Aabkari Times Settings */}
          <div className="form-section mb-8 bg-white p-6 rounded-lg shadow-sm border">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#1e40af' }}>Aabkari Times API Configuration</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <Key size={16} />
                  <span>Brevo API Key</span>
                </Label>
                <Input 
                  type="password" 
                  value={formData.aabkari_api_key} 
                  onChange={(e) => setFormData({ ...formData, aabkari_api_key: e.target.value })} 
                  placeholder={configStatus.aabkari_configured ? "Enter new key to update" : "Paste API key (xkeysib-...)"} 
                />
                <p className="text-xs text-gray-500">{configStatus.aabkari_configured ? "Leave blank to keep existing key" : "Required to send emails"}</p>
              </div>
            </div>
          </div>

          {/* Sugar Times Settings */}
          <div className="form-section mb-8 bg-white p-6 rounded-lg shadow-sm border">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#059669' }}>Sugar Times API Configuration</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <Key size={16} />
                  <span>Brevo API Key</span>
                </Label>
                <Input 
                  type="password" 
                  value={formData.sugar_api_key} 
                  onChange={(e) => setFormData({ ...formData, sugar_api_key: e.target.value })} 
                  placeholder={configStatus.sugar_configured ? "Enter new key to update" : "Paste API key (xkeysib-...)"} 
                />
                <p className="text-xs text-gray-500">{configStatus.sugar_configured ? "Leave blank to keep existing key" : "Required to send emails"}</p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <Button 
            onClick={handleSave} 
            disabled={loading} 
            className="w-full mb-8" 
            style={{ backgroundColor: '#7c3aed' }}
          >
            <Save className="mr-2" size={18} />
            {loading ? 'Saving...' : 'Save API Settings'}
          </Button>

          {/* Test Email Section */}
          {(configStatus.aabkari_configured || configStatus.sugar_configured) && (
            <div className="form-section mb-8 bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#7c3aed' }}>Send Test Email</h2>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Magazine</Label>
                    <select
                      value={testMagazine}
                      onChange={(e) => setTestMagazine(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      {configStatus.aabkari_configured && <option value="Aabkari Times">Aabkari Times</option>}
                      {configStatus.sugar_configured && <option value="Sugar Times">Sugar Times</option>}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Test Email Address</Label>
                    <Input 
                      type="email" 
                      value={testEmail} 
                      onChange={(e) => setTestEmail(e.target.value)} 
                      placeholder="Enter email to receive test" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>&nbsp;</Label>
                    <Button 
                      onClick={handleSendTestEmail} 
                      disabled={testLoading}
                      className="w-full"
                      style={{ backgroundColor: '#059669' }}
                    >
                      <TestTube className="mr-2" size={18} />
                      {testLoading ? 'Sending...' : 'Send Test Email'}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-3">
                  Send a test email to verify your Brevo configuration is working correctly.
                </p>
              </div>
            </div>
          )}

          {/* Next Steps */}
          {(configStatus.aabkari_configured || configStatus.sugar_configured) && (
            <div className="bg-green-50 p-6 rounded-lg border border-green-200">
              <h3 className="font-bold text-green-800 mb-3">Next Steps:</h3>
              <ol className="space-y-2 text-sm text-green-700">
                <li>1. Send a test email above to verify configuration</li>
                <li>2. Go to Renewal Reminders to send subscription reminders</li>
                <li>3. View Email Reports to track all sent emails</li>
              </ol>
              <div className="flex space-x-4 mt-4">
                <Button onClick={() => navigate('/renewal-reminders')} style={{ backgroundColor: '#059669' }}>
                  <Mail className="mr-2" size={18} />
                  Renewal Reminders
                </Button>
                <Button onClick={() => navigate('/email-reports')} variant="outline">
                  <Send className="mr-2" size={18} />
                  Email Reports
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
